/**
 * FeedbackBar — subtle thumbs-up / thumbs-down affordance rendered below each
 * assistant response. Captures user feedback into the chat_feedback table for
 * evaluation and regression detection.
 *
 * Behaviour:
 *  - Click 👍 → posts {rating: 'up'} immediately, fills brand-green, shows a
 *    transient "Thanks" tick.
 *  - Click 👎 → fills muted red, opens an optional comment textarea with
 *    Skip / Submit actions.
 *      • Submit → posts {rating: 'down', comment}.
 *      • Skip   → posts {rating: 'down'} with no comment (the rating still
 *                 counts, the qualitative signal is just empty).
 *  - Click the currently-active button again → DELETE the row (toggle-off).
 *
 * State persistence: the parent passes `initialRating` / `initialId` so a
 * previously-recorded rating paints correctly on session reload.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mergeStyleSets } from '@fluentui/react';
import {
  feedbackService,
  type FeedbackRating,
  type FeedbackSubmitPayload,
} from '../../services/feedbackService';

// Anything the parent already has handy that adds signal to the eval table.
// All optional — the rating itself is the only required signal.
export interface FeedbackBarPayloadExtras {
  query?: string | null;
  response?: string | null;
  sqlText?: string | null;
  rowCount?: number | null;
  trace?: unknown;
  model?: string | null;
  responseTimeMs?: number | null;
}

interface Props {
  agentId: string;
  sessionId: string;
  messageId: string;
  extras?: FeedbackBarPayloadExtras;
  initialRating?: FeedbackRating | null;
  initialId?: string | null;
}

const styles = mergeStyleSets({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    paddingTop: 12,
    borderTop: '1px solid #F1F3F5',
    minHeight: 28,
  },
  btn: {
    width: 28,
    height: 28,
    border: 'none',
    background: 'transparent',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6E7480',
    transition: 'all 120ms ease',
    padding: 0,
    selectors: {
      ':hover': { background: '#F1F3F5', color: '#424751' },
      ':focus-visible': {
        outline: '2px solid #2e873d',
        outlineOffset: 1,
      },
      ':disabled': { cursor: 'default', opacity: 0.5 },
    },
  },
  btnUpActive: {
    color: '#2e873d',
    background: '#f3fcf3',
    selectors: { ':hover': { background: '#f3fcf3', color: '#2e873d' } },
  },
  btnDownActive: {
    color: '#b3261e',
    background: '#fdecea',
    selectors: { ':hover': { background: '#fdecea', color: '#b3261e' } },
  },
  thanks: {
    fontSize: 12,
    color: '#1f6b2c',
    marginLeft: 6,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontFamily: 'Roboto, "Segoe UI", system-ui, sans-serif',
    animation: 'fb-fadein 160ms ease',
  },
  err: {
    fontSize: 12,
    color: '#b3261e',
    marginLeft: 6,
    fontFamily: 'Roboto, "Segoe UI", system-ui, sans-serif',
  },
  comment: {
    marginTop: 10,
    background: '#F1F3F5',
    border: '1px solid #E3E6EA',
    borderRadius: 10,
    padding: '12px 14px',
    animation: 'fb-slidedown 180ms ease',
  },
  commentLabel: {
    display: 'block',
    fontSize: 12,
    color: '#424751',
    marginBottom: 6,
    fontWeight: 500,
    fontFamily: 'Roboto, "Segoe UI", system-ui, sans-serif',
  },
  textarea: {
    width: '100%',
    minHeight: 56,
    resize: 'vertical',
    border: '1px solid #E3E6EA',
    borderRadius: 6,
    padding: '8px 10px',
    fontFamily: 'Roboto, "Segoe UI", system-ui, sans-serif',
    fontSize: 13,
    lineHeight: '1.45', // string — Fluent mergeStyleSets auto-pxifies numbers
    color: '#1f2328',
    background: '#FFFFFF',
    outline: 'none',
    boxSizing: 'border-box',
    selectors: {
      ':focus': {
        borderColor: '#2e873d',
        boxShadow: '0 0 0 2px #f3fcf3',
      },
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
  action: {
    fontSize: 12.5,
    padding: '6px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    border: '1px solid #E3E6EA',
    background: '#FFFFFF',
    color: '#424751',
    fontFamily: 'Roboto, "Segoe UI", system-ui, sans-serif',
    selectors: {
      ':hover': { background: '#F1F3F5' },
      ':disabled': { cursor: 'default', opacity: 0.5 },
    },
  },
  actionPrimary: {
    background: '#2e873d',
    color: '#FFFFFF',
    borderColor: '#2e873d',
    selectors: {
      ':hover': { background: '#1f6b2c', borderColor: '#1f6b2c' },
      ':disabled': { cursor: 'default', opacity: 0.5 },
    },
  },
});

// Keyframes — Fluent mergeStyleSets doesn't emit @keyframes blocks, so we
// inject them once at module load.
if (typeof document !== 'undefined' && !document.getElementById('fb-anim-css')) {
  const tag = document.createElement('style');
  tag.id = 'fb-anim-css';
  tag.textContent = `
@keyframes fb-fadein { from { opacity: 0; } to { opacity: 1; } }
@keyframes fb-slidedown {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}`;
  document.head.appendChild(tag);
}

const ThumbUpIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 1.5 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3z" />
    <path d="M7 11l4-8a2 2 0 0 1 3.6 1.7l-1.2 4.3h5.4a2 2 0 0 1 2 2.3l-1.1 6.4a2 2 0 0 1-2 1.7H7" />
  </svg>
);

const ThumbDownIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 1.5 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3z" />
    <path d="M17 13l-4 8a2 2 0 0 1-3.6-1.7l1.2-4.3H5.2a2 2 0 0 1-2-2.3l1.1-6.4a2 2 0 0 1 2-1.7H17" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const FeedbackBar: React.FC<Props> = ({
  agentId,
  sessionId,
  messageId,
  extras,
  initialRating = null,
  initialId = null,
}) => {
  const [rating, setRating] = useState<FeedbackRating | null>(initialRating);
  const [rowId, setRowId] = useState<string | null>(initialId);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [thanks, setThanks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const thanksTimer = useRef<number | null>(null);

  // Reflect parent-supplied initial state when the underlying message changes
  // (e.g. UnifiedChat rebuilds messages on session select).
  useEffect(() => {
    setRating(initialRating);
    setRowId(initialId);
    setCommentOpen(false);
    setComment('');
    setError(null);
  }, [messageId, initialRating, initialId]);

  useEffect(() => () => {
    if (thanksTimer.current) window.clearTimeout(thanksTimer.current);
  }, []);

  const flashThanks = useCallback(() => {
    setThanks(true);
    if (thanksTimer.current) window.clearTimeout(thanksTimer.current);
    thanksTimer.current = window.setTimeout(() => setThanks(false), 2400);
  }, []);

  const buildPayload = useCallback(
    (r: FeedbackRating, c?: string | null): FeedbackSubmitPayload => ({
      agentId,
      sessionId,
      messageId,
      rating: r,
      comment: c || null,
      query: extras?.query ?? null,
      response: extras?.response ?? null,
      sqlText: extras?.sqlText ?? null,
      rowCount: extras?.rowCount ?? null,
      trace: extras?.trace,
      model: extras?.model ?? null,
      responseTimeMs: extras?.responseTimeMs ?? null,
    }),
    [agentId, sessionId, messageId, extras],
  );

  const submit = useCallback(
    async (r: FeedbackRating, c?: string | null) => {
      setSubmitting(true);
      setError(null);
      try {
        const res = await feedbackService.submit(buildPayload(r, c));
        setRowId(res.id);
        setRating(r);
        flashThanks();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to record feedback';
        setError(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [buildPayload, flashThanks],
  );

  const clear = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (rowId) {
        await feedbackService.deleteById(rowId);
      } else {
        await feedbackService.deleteByMessage(sessionId, messageId);
      }
      setRating(null);
      setRowId(null);
      setCommentOpen(false);
      setComment('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to clear feedback';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [rowId, sessionId, messageId]);

  const onThumbUp = useCallback(() => {
    if (submitting) return;
    if (rating === 'up') {
      void clear();
    } else {
      setCommentOpen(false);
      setComment('');
      void submit('up', null);
    }
  }, [submitting, rating, submit, clear]);

  const onThumbDown = useCallback(() => {
    if (submitting) return;
    if (rating === 'down') {
      // Already 👎 — second click means clear.
      void clear();
    } else {
      // First click — record the rating immediately, then open the optional
      // comment box. This way Skip is genuinely "skip" (no extra POST needed)
      // and Submit just updates the same row with a comment.
      setCommentOpen(true);
      setComment('');
      void submit('down', null);
    }
  }, [submitting, rating, submit, clear]);

  const onSubmitComment = useCallback(() => {
    const trimmed = comment.trim();
    if (!trimmed) {
      setCommentOpen(false);
      return;
    }
    void submit('down', trimmed).then(() => setCommentOpen(false));
  }, [comment, submit]);

  const upClass = `${styles.btn} ${rating === 'up' ? styles.btnUpActive : ''}`.trim();
  const downClass = `${styles.btn} ${rating === 'down' ? styles.btnDownActive : ''}`.trim();

  return (
    <>
      <div className={styles.bar} role="group" aria-label="Rate this response">
        <button
          type="button"
          className={upClass}
          onClick={onThumbUp}
          disabled={submitting}
          title={rating === 'up' ? 'Helpful (click to undo)' : 'Helpful response'}
          aria-pressed={rating === 'up'}
          aria-label="Thumbs up"
        >
          <ThumbUpIcon filled={rating === 'up'} />
        </button>
        <button
          type="button"
          className={downClass}
          onClick={onThumbDown}
          disabled={submitting}
          title={rating === 'down' ? 'Bad response (click to undo)' : 'Bad response'}
          aria-pressed={rating === 'down'}
          aria-label="Thumbs down"
        >
          <ThumbDownIcon filled={rating === 'down'} />
        </button>
        {thanks && (
          <span className={styles.thanks}>
            <CheckIcon />
            Thanks — feedback recorded
          </span>
        )}
        {error && <span className={styles.err}>{error}</span>}
      </div>
      {commentOpen && rating === 'down' && (
        <div className={styles.comment}>
          <label className={styles.commentLabel} htmlFor={`fb-comment-${messageId}`}>
            What was wrong? (optional)
          </label>
          <textarea
            id={`fb-comment-${messageId}`}
            className={styles.textarea}
            placeholder="e.g. The number is too high, this should only cover IT - Data not all IT departments…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
            maxLength={4000}
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.action}
              onClick={() => setCommentOpen(false)}
              disabled={submitting}
            >
              Skip
            </button>
            <button
              type="button"
              className={`${styles.action} ${styles.actionPrimary}`}
              onClick={onSubmitComment}
              disabled={submitting}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackBar;
